import React, { useContext, useRef } from 'react'
import { AllHwilComponentType } from '../utils/scenario-types';
import SimulateService from '../../../../service/SimulateService';
import useNavigation from './useNavigation';
import { showToastMessage } from '../../../../utils/commonFunctions';
import { ScenarioStatesProviderContext } from '../providers/StatesProvider';
import { HwilComponentType, ScenarioResponseType } from '../../types';
import useStepper from './useStepper';
import { SCENARIO_STEPS } from '../../constants';

const useHwilApi = () => {
    const context = useContext(ScenarioStatesProviderContext)
    if (context === undefined) {
        throw new Error('useHwilApi must be used within a Scenario States Provider')
    }
    const { setFormData, setLoading, setScenarioResponse } = context
    const { missionId, scenarioId, getQueryParam, closeScenario } = useNavigation()
    const { updateScenarioStep } = useStepper()

    const IntervalRef = useRef<any>(null)

    const getRemoteAppStatus = async (reconnect: boolean): Promise<{ connected: boolean; disconnected: boolean; timeout: boolean }> => {
        const scenarioStep = getQueryParam('step')
        if (scenarioStep !== SCENARIO_STEPS.CONNECT && scenarioStep !== SCENARIO_STEPS.RECONNECT) {
            clearTimeout(IntervalRef.current);
            IntervalRef.current = null;
            return { connected: false, disconnected: false, timeout: true };
        }
        setLoading((prev) => ({
            ...prev,
            execution: reconnect ? 'Reconnecting' : 'Connecting'
        }))
        try {
            const response = await SimulateService.getHwilConnectStatus(missionId, scenarioId, reconnect);
            const remote_applications = response?.data?.data;
            if (!remote_applications) {
                return { connected: false, disconnected: true, timeout: false };
            }
            const connected = remote_applications.every(obj => obj.status === 'CONNECTED');
            const timeout = remote_applications.some(obj => obj.status === 'TIMEOUT');
            const disconnected = !connected && !timeout;

            setFormData((prev) => ({
                ...prev,
                assets: {
                    ...prev.assets,
                    hwilAssets: {
                        ...prev.assets.hwilAssets,
                        status: "completed",
                        data: (prev.assets.hwilAssets.data || []).map((hwilAsset: HwilComponentType) => {
                            const matched = remote_applications.find(
                                (remote_app: any) => remote_app.uniqueId === hwilAsset.uniqueId
                            );
                            return matched ? { ...hwilAsset, status: matched.status } : hwilAsset;
                        }),
                    },
                },
            }));

            if (connected) {
                clearTimeout(IntervalRef.current);
                IntervalRef.current = null;
                setLoading((prev) => ({
                    ...prev,
                    execution: false
                }))
                const scenarioStep = getQueryParam('step')
                if (scenarioStep === SCENARIO_STEPS.CONNECT || scenarioStep === SCENARIO_STEPS.RECONNECT) {
                    updateScenarioStep(SCENARIO_STEPS.START_SIMULATION)
                }
            } else if (timeout) {
                clearTimeout(IntervalRef.current);
                IntervalRef.current = null;
                setLoading((prev) => ({
                    ...prev,
                    execution: false
                }))
                const scenarioStep = getQueryParam('step')
                if (scenarioStep === SCENARIO_STEPS.CONNECT || scenarioStep === SCENARIO_STEPS.RECONNECT) {
                    showToastMessage('error', 'Failed to Connect to device');
                    updateScenarioStep(SCENARIO_STEPS.RECONNECT)
                }
            }
            return { connected, disconnected, timeout };
        } catch (error) {
            clearTimeout(IntervalRef.current);
            IntervalRef.current = null;
            setLoading((prev) => ({
                ...prev,
                execution: false
            }))
            const scenarioStep = getQueryParam('step')
            if (scenarioStep === SCENARIO_STEPS.CONNECT || scenarioStep === SCENARIO_STEPS.RECONNECT) {
                showToastMessage('error', 'Failed to Connect to device');
                updateScenarioStep(SCENARIO_STEPS.RECONNECT)
            }
            return { connected: false, disconnected: false, timeout: true };
        }
    };

    // TODO: Temporary solution — currently using polling to check remote app connection status.
    // This should be replaced with a WebSocket-based implementation as per AT-9947:    
    // https://antaris.atlassian.net/browse/AT-9947
    const getRemoteAppConnectionStatus = async (reconnect: boolean) => {
        // set_loading('Connecting')
        const result = await getRemoteAppStatus(reconnect);
        if (result.connected || result.timeout) return
        const pollStatus = async () => {
            await getRemoteAppStatus(false)
            if (IntervalRef.current) {
                IntervalRef.current = setTimeout(pollStatus, 5000);
            }
        };
        IntervalRef.current = setTimeout(pollStatus, 5000);
    };

    const getHWILComponents = async (): Promise<AllHwilComponentType> => {
        try {
            const response = await SimulateService.getHWILComponents(missionId, scenarioId)
            const components = response?.data?.data as AllHwilComponentType;
            return components;
        } catch (error) {
            console.log(error);
            showToastMessage("error", "Error fetching HWIL components")
            return {
                payload: [],
                bus: []
            }
        }
    }

    const generateHwilAssets = (): Promise<{ scenario: ScenarioResponseType; hwilAssets: HwilComponentType[] }> => {
        setLoading((prev) => ({
            ...prev,
            execution: 'Configuring'
        }))
        return new Promise(async (resolve, reject) => {
            try {
                const response = await SimulateService.generateHwilConfigFile(missionId, scenarioId);
                const scenario: ScenarioResponseType = response?.data;
                setFormData((prev) => ({
                    ...prev,
                    hwil: {
                        ...prev?.hwil,
                        hwInfo: scenario.hwInfo || [],
                    },
                    assets: {
                        ...prev?.assets,
                        hwilAssets: {
                            status: 'completed',
                            data: scenario.hwInfo || []
                        }
                    }
                }))
                updateScenarioStep(SCENARIO_STEPS.CONNECT)
                setScenarioResponse(scenario)
                return resolve({
                    scenario,
                    hwilAssets: scenario.hwInfo || []
                });
            } catch (error) {
                return reject(error);
            } finally {
                setLoading((prev) => ({
                    ...prev,
                    execution: false
                }))
            }
        });
    };

    const startSimulation = async () => {
        setLoading((prev) => ({
            ...prev,
            execution: 'Starting'
        }))
        try {
            const response = await SimulateService.hwilSimulationStart(missionId, scenarioId)
            const scenarioStep = getQueryParam('step')
            if (response && scenarioStep === SCENARIO_STEPS.START_SIMULATION) {
                showToastMessage('success', "Simulation started successfully");
                closeScenario()
            }
        } catch (error: any) {
            const scenarioStep = getQueryParam('step')
            if (scenarioStep === SCENARIO_STEPS.START_SIMULATION) {
                updateScenarioStep(SCENARIO_STEPS.RECONNECT)
            }
        } finally {
            setLoading((prev) => ({
                ...prev,
                execution: false
            }))
        }
    }


    return {
        startSimulation,
        getHWILComponents,
        generateHwilAssets,
        getRemoteAppConnectionStatus
    }
}

export default useHwilApi